import { useTranslation } from 'react-i18next'
import type { DirectoryUser } from '@/api/directory'
import { ASSIGNEE_NOBODY } from '@/api/leads'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { userLabel } from '@/lib/userDisplay'

/** Radix has no value for "no filter", and an empty string is not one it accepts. */
const ALL = 'ALL'

/**
 * Narrowing the lead list by who works a lead.
 *
 * "Mine" comes first and under its own name, because it is what most people want most of the
 * time — and it is not the same as the "only mine" box, which also counts the leads somebody wrote
 * and handed on. The colleagues who can no longer log in stay in the list and say so: they are
 * exactly the ones whose leads somebody is looking for.
 */
export function AssigneeFilter({
  value,
  onChange,
  colleagues,
  meId,
}: {
  /** A user id, `ASSIGNEE_NOBODY`, or null for every assignment. */
  value: string | null
  onChange: (assignee: string | null) => void
  colleagues: DirectoryUser[]
  meId: string | undefined
}) {
  const { t } = useTranslation()
  const others = colleagues.filter((colleague) => colleague.id !== meId)

  return (
    <Select
      value={value ?? ALL}
      onValueChange={(next) => next && onChange(next === ALL ? null : next)}
    >
      <SelectTrigger className="w-48" aria-label={t('leads.assigneeFilterLabel')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{t('leads.assigneeFilterAll')}</SelectItem>
        {meId && <SelectItem value={meId}>{t('leads.assigneeFilterMe')}</SelectItem>}
        <SelectItem value={ASSIGNEE_NOBODY}>{t('leads.assigneeFilterNobody')}</SelectItem>
        {others.length > 0 && <SelectSeparator />}
        {others.map((colleague) => {
          const name = userLabel(colleague, colleague.id)
          return (
            <SelectItem key={colleague.id} value={colleague.id} textValue={name}>
              {name}
              {!colleague.assignable && ` · ${t('leads.assignee.inactive')}`}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}
